import { Box, Button, ColumnLayout, Container, SpaceBetween, StatusIndicator, TextFilter } from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import TPEAction from 'src/models/common/TPEAction';
import { TPELoadingSpinner } from '../../shared/TPELoadingSpinner';
import ServiceCollection from 'src/services/ServiceCollection';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { MECStatusState, initialState } from 'src/services/mec/status/MECStatusState';
import { mecStatusReducer, ACTIONS } from 'src/services/mec/status/MECStatusReducer';
import '../MEC.scss';
import StatusGrid from './StatusGrid';
import CLIDetailsGrid from './CLIDetailsGrid';
import CONSTANTS, {REVERSAL_ALLOWED_EXECUTION_STATUSES} from "src/utils/constants";
import ATPHorizontalRadioGroup from 'src/components/shared/ATPHorizontalRadioGroup';
import ReversalsContainer from './ReversalsContainer';
import ReversalAutosuggest from './ReversalAutosuggest';
import TPEErrorWatcher from 'src/components/shared/TPEErrorWatcher';
import ArrayUtils from 'src/utils/arrayUtils';
import { MECStatusDataRow } from "src/models/mec/status/GetMECStatusDataResult";
import { formatMECStatusDataResults } from "src/services/mec/MECService";
import GetMECCLIDetailsResult, {MECCLIDetailsRow} from "src/models/mec/status/GetMECCLIDetailsResult";
import {UserProfile} from "src/models/users/UserProfile";
import {GlobalAppContext} from "src/components/App";
import {ExecuteBulkReversalWSMessage, ExecuteCLIWebSocketMessage} from "src/models/mec/status/MECWebSocketMessages";
import moment from 'moment';

export type ContextType = {
    state: MECStatusState,
    dispatch: React.Dispatch<TPEAction>,
    services: ServiceCollection,
    userProfile: UserProfile,
}

const MECStatusProvider = (props: any) => {
    const { state, dispatch, services, children, userProfile } = props;
    const providerValue = React.useMemo(() => ({
        state, dispatch, services, userProfile
    }), [state, dispatch]);
    return (
        <MECStatusContext.Provider value={providerValue}>
            {children}
        </MECStatusContext.Provider>
    );
}
export const MECStatusContext = React.createContext(null as unknown as ContextType);

export default function RecordsView(props: {services: ServiceCollection}) {
    const { services } = props;
    const [state, dispatch] = useReducerWithLogger(mecStatusReducer, initialState);
    const { group, reversals = [], reversalSearchItems = [], statusResult } = state;
    const period = moment().subtract(1, 'months').format(CONSTANTS.MEC_PERIOD_FORMAT);
    const { userProfile } = React.useContext(GlobalAppContext);

    const reversalSearchOptions = [
        { value: CONSTANTS.REVERSAL_SEARCH_OPTIONS.WORKBOOK, label: CONSTANTS.REVERSAL_SEARCH_OPTIONS.WORKBOOK },
        { value: CONSTANTS.REVERSAL_SEARCH_OPTIONS.CLI, label: CONSTANTS.REVERSAL_SEARCH_OPTIONS.CLI }];

    const [statusSearchText, setStatusSearchText] = useState('');
    const [cliSearchText, setCLISearchText] = useState('');
    const [selectedReversalSearchOption, setSelectedReversalSearchOption] = useState(CONSTANTS.REVERSAL_SEARCH_OPTIONS.WORKBOOK);
    const [reversalSearchText, setReversalSearchText] = useState(null as unknown as string);
    const [reversalSearchDisplayText, setReversalSearchDisplayText] = useState(null as unknown as string);
    const [loadTime, setLoadTime] = useState((new Date()).toString());
    const [statusLoadTime, setStatusLoadTime] = useState((new Date()).toString());
    const [triggerSearchForReversal, setTriggerSearchForReversal] = useState(null as unknown as string);
    const [statusActionButtonIsDisabled, setStatusActionButtonIsDisabled] = useState(true);
    const [selectedGroups, setSelectedGroups] = useState<MECStatusDataRow[]>([]);
    const [selectedCLIs, setSelectedCLIs] = useState<MECCLIDetailsRow[]>([]);
    const [statusSearchCount, setStatusSearchCount] = useState(0);
    const [cliDetailsSearchCount, setCliDetailsSearchCount] = useState(0);
    const [showCLIGrid, setShowCLIGrid] = useState(false);

    const [mecStatusResult, groupExecutionStatus, isGettingMECStatus, mecStatusError] = services.mecService.getMECStatusData(statusLoadTime, period);
    const [cliDetailsResult, isGettingCLIDetails, cliDetailsError] = services.mecService.getGroupExecutionCLIDetails(group, period);
    const [reversalsResult, isGettingReversals, reversalsError] = services.mecService.getBulkReversals(statusLoadTime, period);
    const [clisResult, isGettingCLIs, cliError] = services.cliService.getCLIs(loadTime);
    const [workbooksResult, isGettingWorkbooks, workbooksError] = services.mecService.getWorkbooks(loadTime);
    const [searchItemForReversalResult, isSearchingItemForReversal, searchItemForReversalError] = services.mecService.searchItemForReversal(
        triggerSearchForReversal,
        period,
        selectedReversalSearchOption == CONSTANTS.REVERSAL_SEARCH_OPTIONS.WORKBOOK ? reversalSearchText : undefined,
        selectedReversalSearchOption == CONSTANTS.REVERSAL_SEARCH_OPTIONS.CLI ? reversalSearchText : undefined);

    //TODO: will change once search groups API is completed
    const searchGroups = (searchText:string) => {
        const search = searchText.toLowerCase();
        const statusSearchRows = groupExecutionStatus.filter(record => record.group.toLowerCase().startsWith(search));
        const results = formatMECStatusDataResults(statusSearchRows);

        console.log(results);

        dispatch(ACTIONS.SET_STATUS_RESULT.withPayload(results))
        setStatusSearchCount(statusSearchRows.length);

        if(searchText.length == 0){
            dispatch(ACTIONS.SET_STATUS_RESULT.withPayload(mecStatusResult));
            setStatusSearchCount(mecStatusResult.records.length);
        }
    }

    //TODO: will change once search cliDetails API is completed
    const searchCLIs = (searchText:string) => {
        const search = searchText.toLowerCase();
        const cliSearchRows = cliDetailsResult.cliDetails
            .filter(cliDetail => cliDetail.calculationNumber.toLowerCase().startsWith(search));
        const results: GetMECCLIDetailsResult = {
            cliDetails: cliSearchRows
        }

        dispatch(ACTIONS.SET_CLI_DETAILS_RESULT.withPayload(results));
        setCliDetailsSearchCount(cliSearchRows.length);

        if(searchText.length == 0){
            dispatch(ACTIONS.SET_CLI_DETAILS_RESULT.withPayload(cliDetailsResult));
            setCliDetailsSearchCount(cliDetailsResult.cliDetails.length);
        }
    }

    useEffect(() => {
        const webSocketListener = setupWebSocketEvents(services);
        return () => {
            services.atpWebSocketApiService.removeListener('', webSocketListener);
        }
    }, [])

    useEffect(() => {
        if (statusResult != null) {
            const selectedGroups = statusResult.records.filter(record => record.isSelected);
            if (selectedGroups.length > 0) {
                setStatusActionButtonIsDisabled(false);
            } else {
                setStatusActionButtonIsDisabled(true);
            }
            setSelectedGroups(selectedGroups);
        }
    }, [statusResult]);

    useEffect(() => {
        setStatusSearchCount(mecStatusResult == null ? 0 : mecStatusResult.records.length);
        dispatch(ACTIONS.SET_STATUS_RESULT.withPayload(mecStatusResult));
    }, [mecStatusResult]);

    useEffect(() => {
        if (cliDetailsResult && cliDetailsResult.cliDetails.length > 0) {
            dispatch(ACTIONS.SET_CLI_DETAILS_RESULT.withPayload(cliDetailsResult));
            setShowCLIGrid(true);
        }else{
            setShowCLIGrid(false);
        }
    }, [cliDetailsResult]);

    useEffect(() => {
        if (reversalsResult == null) {
            return;
        }
        dispatch(ACTIONS.SET_REVERSALS.withPayload(reversalsResult));
    }, [reversalsResult])

    useEffect(() => {
        if (searchItemForReversalResult == null) {
            return;
        }
        dispatch(ACTIONS.ADD_REVERSAL_SEARCH_ITEM.withPayload(searchItemForReversalResult));
        services.messageService.showSuccessAutoDismissBanner(`${reversalSearchDisplayText} has been successfully added to the reversals table below. If you do not reverse the item, it will be removed from the table when you leave this page.`, 10000);
    }, [searchItemForReversalResult])

    const searchForReversal = (text: string) => {
        if (text == null || text.trim() == '') {
            return;
        }

        if (selectedReversalSearchOption == CONSTANTS.REVERSAL_SEARCH_OPTIONS.WORKBOOK) {
            const workbookId = text.split(':')[0];
            const workbookName = text.replace(`${workbookId}: `, '');

            if (reversalSearchItems.concat(reversals).filter(r => r.workbookId == workbookId).length > 0) {
                services.messageService.showInfoAutoDismissBanner(`${workbookName} is already available in the reversals table below.`, 10000);
                return;
            }

            if (ArrayUtils.isNullOrEmpty(workbooksResult?.filter(w => w.workbookId == workbookId))) {
                services.messageService.showErrorBanner(`Cannot find workbook named ${text}. Please select an option from the workbook list.`);
                return;
            }
            setReversalSearchText(workbookId);
            setReversalSearchDisplayText(workbookName);
        } else {
            if (reversalSearchItems.concat(reversals).filter(r => r.calculationNumber == text).length > 0) {
                services.messageService.showInfoAutoDismissBanner(`${text} is already available in the reversals table below.`, 10000);
                return;
            }

            if (ArrayUtils.isNullOrEmpty(clisResult?.filter(c => c == text))) {
                services.messageService.showErrorBanner(`Cannot find CLI ${text}. Please select an option from the CLI list.`);
                return;
            }
            setReversalSearchText(text);
            setReversalSearchDisplayText(text);
        }
        setTriggerSearchForReversal((new Date()).toString());
    }

    const onSelectCLI = () => {
        setSelectedCLIs(cliDetailsResult.cliDetails.filter(r => r.isSelected));
    }

    const onGroupRun = () => {
        if (selectedGroups.length != 0) {
            selectedGroups.forEach(group => {
                const groupNum = group.dayOrGroup.split(" ");
                services.atpWebSocketApiService.executeGroup({
                    groupNumber: groupNum.length > 1 ? groupNum[1] : group.dayOrGroup,
                })
            });
        }
    }

    const onCLIRun = () => {
        if (selectedCLIs.length != 0) {
            selectedCLIs.forEach(cli => {
                services.atpWebSocketApiService.executeCLI({
                    agreementCLI: cli.calculationNumber
                })
            });
        }
    }

    const onReverse = (itemType: string) => {
        if (itemType == CONSTANTS.REVERSAL_ITEM_TYPES.GROUP) {
            const validGroups = selectedGroups
                .filter(x => REVERSAL_ALLOWED_EXECUTION_STATUSES.includes(x.status))
            const ineligibleGroupCount = selectedGroups.length - validGroups.length;

            executeReversal(validGroups, itemType, 'dayOrGroup', ineligibleGroupCount);
        } else if (itemType == CONSTANTS.REVERSAL_ITEM_TYPES.CLI) {
            const validCLIs = selectedCLIs.filter(cli => REVERSAL_ALLOWED_EXECUTION_STATUSES.includes(cli.status));
            const ineligibleCLICount = selectedCLIs.length - validCLIs.length;

            executeReversal(validCLIs, itemType, 'calculationNumber', ineligibleCLICount);
        }
    }

    const executeReversal = (items: any[], itemType: string, itemIdKey: string, ineligibleItemCount: number) => {
        if (items.length > 0) {
            const reversalRequests = items.map(item => ({
                itemId: itemType?.toUpperCase() == CONSTANTS.REVERSAL_ITEM_TYPES.GROUP.toUpperCase() ? item[itemIdKey]?.split(" ")[1] : item[itemIdKey],
                itemType: itemType.toUpperCase()
            }))

            services.atpWebSocketApiService.executeBulkReversal({
                requests: reversalRequests,
                reversedBy: userProfile.user
            });
            refreshStatus();

            if (ineligibleItemCount == 0) {
                services.messageService.showSuccessAutoDismissBanner(`${items.length} items submitted for reversal.`, 3000);
            } else {
                services.messageService.showWarningBanner(`${items.length} items submitted for reversal, ${ineligibleItemCount} items ineligible for reversal.`);
            }
        } else {
            services.messageService.showErrorBanner('None of the selected items are eligible for reversal.')
        }
    }

    const refreshStatus = () => {
        setStatusLoadTime(new Date().toString());
        dispatch(ACTIONS.SET_GROUP.withPayload(null));
        setShowCLIGrid(false);
    }

    return <MECStatusProvider services={services} state={state} dispatch={dispatch} userProfile = {userProfile}>
        <SpaceBetween direction="vertical" size="m">
            <div className="mecStatusActionBar">
                <div className="reversalSearchContainer">
                    <ATPHorizontalRadioGroup
                        data-class="basicModalRadioGroup"
                        onChange={setSelectedReversalSearchOption}
                        value={selectedReversalSearchOption}
                        items={reversalSearchOptions}
                    />
                    <ReversalAutosuggest
                        filterType={selectedReversalSearchOption}
                        workbooks={workbooksResult}
                        clis={clisResult}
                        onSelect={searchForReversal}
                        loading={isGettingWorkbooks || isGettingCLIs}
                        disabled={isGettingWorkbooks || isGettingCLIs || isSearchingItemForReversal}
                    />
                    {(isGettingWorkbooks || isGettingCLIs || isSearchingItemForReversal) &&
                        <Box padding={{left: 'xs', top: 'xxs'}}>
                            <StatusIndicator data-class="smallStatusIndicator" type="loading">
                                {isSearchingItemForReversal ? 'Searching...' : 'Loading options...'}
                            </StatusIndicator>
                        </Box>
                    }
                </div>
                <div>
                    <Box float={'right'}>
                        <Button variant="normal" onClick={refreshStatus} iconName={"refresh"}></Button>
                    </Box>
                </div>
            </div>
            {(reversals.length > 0 || reversalSearchItems.length > 0) && <ReversalsContainer loading={isGettingReversals} services={services} userId={userProfile.user} refreshStatus={refreshStatus}/>}
            <Container data-class="containerWithHeaderAndGrid">
                <div className="containerHeader">
                    <ColumnLayout columns={2} data-class="fullColumnLayout">
                        <Box variant="h2">Status</Box>
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="s">
                                <Button variant="normal" disabled={statusActionButtonIsDisabled} onClick={() => onReverse(CONSTANTS.REVERSAL_ITEM_TYPES.GROUP)}>Reverse</Button>
                                <Button variant="normal" disabled={true}>Pause</Button>
                                <Button variant="normal" disabled={statusActionButtonIsDisabled} onClick={onGroupRun}>Run</Button>
                            </SpaceBetween>
                        </Box>
                        <TextFilter data-class="basicTextFilter"
                            filteringText={statusSearchText}
                            onDelayedChange={({ detail }) => searchGroups(detail.filteringText)}
                            onChange={({ detail }) => setStatusSearchText(detail.filteringText)}
                            filteringPlaceholder='Search'
                            filteringAriaLabel='Search'
                            countText= {statusSearchCount + ' matches found'}
                        />
                    </ColumnLayout>
                </div>
                <TPELoadingSpinner loading={isGettingMECStatus} loadingText='Loading'>
                    <div className="mecTableContainer">
                        <StatusGrid loading={isGettingMECStatus}/>
                    </div>
                </TPELoadingSpinner>
            </Container>
            {showCLIGrid && <Container data-class="containerWithHeaderAndGrid">
                <div className="containerHeader">
                    <ColumnLayout columns={2} data-class="fullColumnLayout">
                        <Box variant="h2">{'CLI Details' + (group ? ' - ' + group : '')}</Box>
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="s">
                                <Button variant="normal" disabled={selectedCLIs.length == 0} onClick={() => onReverse(CONSTANTS.REVERSAL_ITEM_TYPES.CLI)}>Reverse</Button>
                                <Button variant="normal" disabled={true}>Stop from running</Button>
                                <Button variant="normal" disabled={selectedCLIs.length == 0} onClick={() => onCLIRun()}>Run</Button>
                            </SpaceBetween>
                        </Box>
                        <TextFilter data-class="basicTextFilter"
                            filteringText={cliSearchText}
                            onDelayedChange={({ detail }) => searchCLIs(detail.filteringText)}
                            onChange={({ detail }) => setCLISearchText(detail.filteringText)}
                            filteringPlaceholder='Search'
                            filteringAriaLabel='Search'
                            countText= {cliDetailsSearchCount + ' matches found'}
                        />
                    </ColumnLayout>
                </div>
                <div className="tableContainer">
                    <CLIDetailsGrid loading={isGettingCLIDetails} onCLISelect={onSelectCLI} />
                </div>
            </Container>}
        </SpaceBetween>
        <TPEErrorWatcher services={services} errors={[mecStatusError, cliDetailsError, reversalsError, searchItemForReversalError, cliError, workbooksError]} />
    </MECStatusProvider>
}

/**
 * Appends a listener for the execute group (Web Socket) messages. It checks the messages and
 * displays the error if any.
 * @param services The list of services
 */
function setupWebSocketEvents(services: ServiceCollection) {

    const listener = (x: any) => {
        const webSocketMessage = JSON.parse(x);

        //Listen for messages from bulk reversal API
        if (webSocketMessage?.messageType == CONSTANTS.WEB_SOCKET_API_ROUTES.EXECUTE_BULK_REVERSAL) {
            const executeBulkReversalWSMessage = webSocketMessage as ExecuteBulkReversalWSMessage
            const invalidItemCount = Object.keys(executeBulkReversalWSMessage.invalidItems).length;

            if (invalidItemCount > 0)
                services.messageService.showErrorBanner(`Cannot reverse ${invalidItemCount} items: Items either have not been executed for current MEC period or statuses are not valid for reversal.`);
        }else if(webSocketMessage?.messageType == CONSTANTS.WEB_SOCKET_API_ROUTES.EXECUTE_CLI) {
            const executeCLIWSMessage = webSocketMessage as ExecuteCLIWebSocketMessage
            //TODO: Decide what to display
        }

    };
    services.atpWebSocketApiService.addListener(listener);
    return listener;
}